import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastAction } from '@/components/ui/toast';
import { API_CONFIG } from '@/constants/config';
import { toast } from '@/hooks/use-toast';
import { useProject } from '@/hooks/api';
import { resolveObservedProjectId } from '@/lib/access-revocation';
import { realtimeDispatcher } from '@/lib/realtime/realtime-dispatcher';
import { realtimeApi } from '@/services/api/realtime';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { useRealtimeStore } from '@/stores/realtimeStore';
import type { RealtimeClientEvent, RealtimeContext, RealtimeEvent } from '@/types/realtime';

const HEARTBEAT_TIMEOUT_MS = 120_000;
const HEARTBEAT_TIMEOUT_CLOSE_CODE = 4000;
const RECONNECT_DELAYS_MS = [2_000, 4_000, 8_000] as const;

function resolveBaseOrigin(): string {
  return typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
}

function buildRealtimeWebSocketUrl(ticket: string): string {
  const baseUrl = API_CONFIG.BASE_URL || '/api/v1';
  const url = new URL(baseUrl, resolveBaseOrigin());
  const basePath = url.pathname.replace(/\/$/, '');
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = `${basePath}/realtime/ws`;
  url.search = new URLSearchParams({ ticket }).toString();
  return url.toString();
}

function parseRealtimeEvent(raw: string): RealtimeEvent | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const event = parsed as Partial<RealtimeEvent>;
    if (typeof event.topic !== 'string' || typeof event.event !== 'string') return null;
    return {
      eventId: typeof event.eventId === 'string' ? event.eventId : null,
      topic: event.topic,
      event: event.event,
      payload:
        event.payload && typeof event.payload === 'object' && !Array.isArray(event.payload)
          ? (event.payload as Record<string, unknown>)
          : {},
    };
  } catch {
    return null;
  }
}

export function useGlobalRealtime() {
  const location = useLocation();
  const persistedProjectId = useAppStore((state) => state.currentProjectId);
  const currentProjectId = resolveObservedProjectId(
    location.pathname,
    location.search,
    persistedProjectId
  );
  const { data: currentProject } = useProject(currentProjectId);
  const currentProjectParentId = currentProject?.parentProjectId ?? null;
  const currentProjectRealtimeContext = useMemo(
    () =>
      currentProject
        ? {
            orgId: currentProject.orgId ?? null,
            ownerType: currentProject.ownerType ?? null,
            viewScope: currentProject.viewScope ?? null,
            collabDirection: currentProject.collabDirection ?? null,
            type: currentProject.type ?? null,
          }
        : undefined,
    [currentProject]
  );
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const manualRetryRef = useRef<(() => void) | null>(null);
  const degradedToastRef = useRef<ReturnType<typeof toast> | null>(null);

  const contextRef = useRef<RealtimeContext>({
    pathname: location.pathname,
    search: location.search,
    activeOrgId,
    currentProjectId,
    currentProjectParentId,
    currentProject: currentProjectRealtimeContext,
  });

  useEffect(() => {
    contextRef.current = {
      pathname: location.pathname,
      search: location.search,
      activeOrgId,
      currentProjectId,
      currentProjectParentId,
      currentProject: currentProjectRealtimeContext,
    };
  }, [
    activeOrgId,
    currentProjectId,
    currentProjectParentId,
    currentProjectRealtimeContext,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    let active = true;

    const clearReconnectTimer = () => {
      if (!reconnectTimerRef.current) return;
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    };

    const clearWatchdog = () => {
      if (!watchdogTimerRef.current) return;
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    };

    const closeSocket = (code = 1000, reason = 'client close') => {
      const socket = wsRef.current;
      if (!socket) return;
      useRealtimeStore.getState().setSendEvent(null);
      socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
      try {
        socket.close(code, reason);
      } catch {
        // 浏览器可能在连接尚未建立时抛错，忽略后由重连兜底。
      }
      if (wsRef.current === socket) {
        wsRef.current = null;
      }
    };

    const feedWatchdog = () => {
      clearWatchdog();
      watchdogTimerRef.current = setTimeout(() => {
        closeSocket(HEARTBEAT_TIMEOUT_CLOSE_CODE, 'heartbeat timeout');
        scheduleReconnect();
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const showDegradedToast = () => {
      if (degradedToastRef.current) return;
      degradedToastRef.current = toast({
        title: '实时协同连接异常',
        description: '当前页面可能无法自动刷新，请检查网络后重试。',
        variant: 'destructive',
        duration: Infinity,
        action: (
          <ToastAction
            altText="重试"
            onClick={() => {
              degradedToastRef.current?.dismiss();
              degradedToastRef.current = null;
              manualRetryRef.current?.();
            }}
          >
            重试
          </ToastAction>
        ),
      });
    };

    const enterDegraded = () => {
      useRealtimeStore.getState().setConnectionState('degraded');
      useRealtimeStore.getState().setFailureCount(attemptsRef.current);
      useNotificationStore.getState().setConnected(false);
      showDegradedToast();
    };

    const connect = async () => {
      if (!active) return;
      const token = localStorage.getItem('token');
      if (!token) {
        useRealtimeStore.getState().setConnectionState('idle');
        return;
      }

      clearReconnectTimer();
      closeSocket(1000, 'replace socket');
      clearWatchdog();

      try {
        useRealtimeStore.getState().setConnectionState('ticket_refreshing');
        const ticket = await realtimeApi.issueTicket();
        if (!active) return;

        useRealtimeStore
          .getState()
          .setConnectionState(attemptsRef.current > 0 ? 'reconnecting' : 'connecting');
        const socket = new WebSocket(buildRealtimeWebSocketUrl(ticket.ticket));
        wsRef.current = socket;

        socket.onopen = () => {
          if (!active || wsRef.current !== socket) return;
          attemptsRef.current = 0;
          degradedToastRef.current?.dismiss();
          degradedToastRef.current = null;
          useRealtimeStore.getState().setSendEvent((event: RealtimeClientEvent) => {
            if (wsRef.current !== socket || socket.readyState !== WebSocket.OPEN) return false;
            socket.send(JSON.stringify(event));
            return true;
          });
          useRealtimeStore.getState().markConnected();
          useNotificationStore.getState().setConnected(true);
          feedWatchdog();
        };

        socket.onmessage = (message) => {
          if (!active || wsRef.current !== socket) return;
          feedWatchdog();
          useRealtimeStore.getState().markEventReceived();
          const event = parseRealtimeEvent(typeof message.data === 'string' ? message.data : '');
          if (!event || event.topic === 'heartbeat') return;
          void realtimeDispatcher.dispatch(event, contextRef.current);
        };

        socket.onerror = () => {
          if (!active || wsRef.current !== socket) return;
          useRealtimeStore.getState().setConnectionState('reconnecting');
        };

        socket.onclose = () => {
          if (!active || wsRef.current !== socket) return;
          clearWatchdog();
          wsRef.current = null;
          useRealtimeStore.getState().setSendEvent(null);
          useNotificationStore.getState().setConnected(false);
          scheduleReconnect();
        };
      } catch {
        if (!active) return;
        scheduleReconnect();
      }
    };

    function scheduleReconnect() {
      if (!active) return;
      clearReconnectTimer();
      attemptsRef.current += 1;
      useRealtimeStore.getState().setFailureCount(attemptsRef.current);
      if (attemptsRef.current > RECONNECT_DELAYS_MS.length) {
        enterDegraded();
        return;
      }
      useRealtimeStore.getState().setConnectionState('reconnecting');
      reconnectTimerRef.current = setTimeout(
        () => {
          reconnectTimerRef.current = null;
          void connect();
        },
        RECONNECT_DELAYS_MS[attemptsRef.current - 1]
      );
    }

    manualRetryRef.current = () => {
      if (!active) return;
      attemptsRef.current = 0;
      void connect();
    };

    // 预算耗尽后由外部恢复信号重新开启一轮，避免后台无限重试或重复建连。
    const resumeDegradedConnection = () => {
      if (useRealtimeStore.getState().connectionState !== 'degraded') return;
      manualRetryRef.current?.();
    };
    window.addEventListener('online', resumeDegradedConnection);
    window.addEventListener('focus', resumeDegradedConnection);

    void connect();

    return () => {
      active = false;
      window.removeEventListener('online', resumeDegradedConnection);
      window.removeEventListener('focus', resumeDegradedConnection);
      manualRetryRef.current = null;
      degradedToastRef.current?.dismiss();
      degradedToastRef.current = null;
      clearReconnectTimer();
      clearWatchdog();
      closeSocket(1000, 'component unmount');
      useRealtimeStore.getState().setSendEvent(null);
      useRealtimeStore.getState().setConnectionState('closed');
      useNotificationStore.getState().setConnected(false);
    };
  }, []);
}
